import { useState, useEffect } from "react";
import AdminLayout from "@/components/layout/AdminLayout";
import API from "@/lib/api";
import { useLanguage } from "@/contexts/LanguageContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Search, CreditCard, ChevronLeft, ChevronRight } from "lucide-react";

export default function AdminRekenings() {
  const [rekenings, setRekenings] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ bank_name: "", account_number: "", account_holder: "", status: "active" });
  const { t } = useLanguage();
  
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const fetchRekenings = () => API.get("/rekenings").then((r) => setRekenings(r.data.rekenings)).catch(() => {});
  useEffect(() => { fetchRekenings(); }, []);

  const filteredRekenings = rekenings.filter((r) => 
    r.bank_name.toLowerCase().includes(searchQuery.toLowerCase()) || 
    r.account_number.includes(searchQuery) ||
    r.account_holder.toLowerCase().includes(searchQuery.toLowerCase())
  );
  const totalPages = Math.max(1, Math.ceil(filteredRekenings.length / itemsPerPage));
  const paginatedRekenings = filteredRekenings.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  useEffect(() => { setCurrentPage(1); }, [searchQuery]);

  const openCreate = () => { setEditing(null); setForm({ bank_name: "", account_number: "", account_holder: "", status: "active" }); setOpen(true); };
  const openEdit = (r) => { setEditing(r.rekening_id); setForm({ bank_name: r.bank_name, account_number: r.account_number, account_holder: r.account_holder, status: r.status }); setOpen(true); };

  const handleSave = async () => {
    try {
      if (editing) {
        await API.put(`/rekenings/${editing}`, form);
        toast.success(t("admin_rekenings.updated"));
      } else {
        await API.post("/rekenings", form);
        toast.success(t("admin_rekenings.created"));
      }
      setOpen(false);
      fetchRekenings();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save"); }
  };

  return (
    <AdminLayout title={t("admin_rekenings.title")}>
      <div data-testid="admin-rekenings">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <p className="text-sm text-stone-500 whitespace-nowrap">{filteredRekenings.length} {t("admin_rekenings.title")}</p>
          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
            <div className="relative flex-1 sm:w-64">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-4 w-4 text-stone-400" />
              </div>
              <Input
                type="text"
                placeholder={t("common.search")}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 h-9"
              />
            </div>
            <button onClick={openCreate} className="bg-[#8A9A86] text-white px-4 h-9 text-sm flex items-center justify-center gap-2 hover:bg-[#768573] rounded-md transition-colors whitespace-nowrap">
              <Plus className="w-4 h-4" /> {t("admin_rekenings.add_button")}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          {paginatedRekenings.map((r) => (
            <div key={r.rekening_id} className={`bg-white border p-6 flex flex-col justify-between ${r.status === 'active' ? 'border-stone-100' : 'border-stone-200 bg-stone-50'}`}>
              <div>
                <div className="flex justify-between items-start mb-3 gap-3">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <CreditCard className="w-5 h-5 text-stone-400 shrink-0" />
                    <h3 className="font-serif text-lg text-stone-900 truncate">{r.bank_name}</h3>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <button onClick={() => openEdit(r)} className="text-stone-400 hover:text-stone-700 p-1"><Pencil className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
                <p className="text-xl font-mono tracking-wider text-stone-700 mb-2">{r.account_number}</p>
                <p className="text-sm text-stone-500 mb-4">{r.account_holder}</p>
                <div className="mb-2">
                  <span className={`text-[10px] font-semibold tracking-wide uppercase px-2 py-1 rounded inline-flex items-center gap-1.5 ${r.status === 'active' ? 'text-emerald-700 bg-emerald-50' : 'text-stone-500 bg-stone-200'}`}>
                    {r.status}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between border-t border-stone-100 pt-4 mt-6">
            <p className="text-sm text-stone-500">{t("common.page")} {currentPage} {t("common.of")} {totalPages}</p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="p-1.5 border border-stone-200 rounded text-stone-500 hover:bg-stone-50 disabled:opacity-50"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="p-1.5 border border-stone-200 rounded text-stone-500 hover:bg-stone-50 disabled:opacity-50"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="bg-white">
            <DialogHeader><DialogTitle className="font-serif text-xl">{editing ? t("admin_rekenings.edit_title") : t("admin_rekenings.new_title")}</DialogTitle></DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label className="text-xs uppercase tracking-wider text-stone-500">{t("admin_rekenings.bank_name")}</Label>
                <Input value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} className="mt-1" placeholder={t("admin_rekenings.placeholder_bank")} />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-stone-500">{t("admin_rekenings.account_number")}</Label>
                <Input value={form.account_number} onChange={(e) => setForm({ ...form, account_number: e.target.value })} className="mt-1 font-mono tracking-wider" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-stone-500">{t("admin_rekenings.account_holder")}</Label>
                <Input value={form.account_holder} onChange={(e) => setForm({ ...form, account_holder: e.target.value })} className="mt-1" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-stone-500">{t("admin_rekenings.status")}</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <button onClick={handleSave} className="w-full bg-[#8A9A86] text-white py-3 text-sm uppercase tracking-wider hover:bg-[#768573]" data-testid="save-rek-btn">
                {editing ? t("admin_rekenings.update_btn") : t("admin_rekenings.create_btn")}
              </button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
